import React, { Children, Fragment } from 'react';
import { motion } from 'framer-motion';
import { SlideData } from '../data/slides';
import { CostCalculator } from './CostCalculator';
const FormattedText = ({
  text,
  className = ''
}: {
  text: string;
  className?: string;
}) => {
  if (!text.includes('™')) return <span className={className} data-id="element-141">{text}</span>;
  const parts = text.split('™');
  return <span className={className} data-id="element-142">
      {parts.map((part, i) => <Fragment key={i} data-id="element-143">
          {part}
          {i < parts.length - 1 && <sup className="text-[0.6em] font-medium" data-id="element-144">™</sup>}
        </Fragment>)}
    </span>;
};
const SnakkerLogo = ({
  className = ''
}: {
  className?: string;
}) => <div className={`text-2xl font-bold tracking-tight text-[#6C2BD9] ${className}`} data-id="element-145">
    Snakker
  </div>;
const containerVariants = {
  hidden: {
    opacity: 0
  },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.1
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: [0.16, 1, 0.3, 1]
    }
  }
};
export const SlideRenderer = ({
  slide
}: {
  slide: SlideData;
}) => {
  const renderContent = () => {
    switch (slide.type) {
      case 'title':
        return <div className="flex flex-col items-center justify-center text-center h-full max-w-5xl mx-auto px-8" data-id="element-146">
            <motion.h1 variants={itemVariants} className="text-5xl md:text-7xl font-bold text-slate-900 tracking-tight mb-6" data-id="element-147">
              <FormattedText text={slide.headline || ''} data-id="element-148" />
            </motion.h1>
            {slide.subtext && <motion.p variants={itemVariants} className="text-2xl md:text-3xl text-slate-500 font-light mb-12" data-id="element-149">
                <FormattedText text={slide.subtext} data-id="element-150" />
              </motion.p>}
            {slide.tagline && <motion.div variants={itemVariants} className="mt-8" data-id="element-151">
                <span className="inline-block px-6 py-3 bg-[#6C2BD9]/10 text-[#6C2BD9] rounded-full text-lg md:text-xl font-medium" data-id="element-152">
                  {slide.tagline}
                </span>
              </motion.div>}
          </div>;
      case 'statement':
        return <div className="flex flex-col items-center justify-center text-center h-full max-w-4xl mx-auto px-8 relative" data-id="element-153">
            {slide.showLogo && <motion.div variants={itemVariants} className="absolute top-0" data-id="element-154">
                <SnakkerLogo data-id="element-155" />
              </motion.div>}
            <motion.h2 variants={itemVariants} className="text-4xl md:text-6xl font-bold text-slate-900 tracking-tight leading-tight mb-8" data-id="element-156">
              <FormattedText text={slide.headline || ''} data-id="element-157" />
            </motion.h2>
            {slide.subtext && <motion.p variants={itemVariants} className="text-2xl md:text-3xl text-[#6C2BD9] font-medium mb-8" data-id="element-158">
                <FormattedText text={slide.subtext} data-id="element-159" />
              </motion.p>}
            {slide.body && <motion.p variants={itemVariants} className="text-xl md:text-2xl text-slate-600 mb-12" data-id="element-160">
                <FormattedText text={slide.body} data-id="element-161" />
              </motion.p>}
            {slide.mutedLines && <motion.div variants={itemVariants} className="flex flex-col gap-4 mt-8" data-id="element-162">
                {slide.mutedLines.map((line, i) => <p key={i} className="text-lg md:text-xl text-slate-400 font-light" data-id="element-163">
                    {line}
                  </p>)}
              </motion.div>}
          </div>;
      case 'bullets':
        return <div className="flex flex-col justify-center h-full max-w-5xl mx-auto px-8 md:px-16 w-full" data-id="element-164">
            <motion.h2 variants={itemVariants} className="text-4xl md:text-5xl font-bold text-slate-900 tracking-tight mb-4" data-id="element-165">
              <FormattedText text={slide.headline || ''} data-id="element-166" />
            </motion.h2>
            {slide.subtext && <motion.p variants={itemVariants} className="text-xl md:text-2xl text-slate-500 mb-12" data-id="element-167">
                <FormattedText text={slide.subtext} data-id="element-168" />
              </motion.p>}
            <div className={`flex flex-col gap-6 ${!slide.subtext ? 'mt-12' : ''}`} data-id="element-169">
              {slide.bullets?.map((bullet, i) => <motion.div key={i} variants={itemVariants} className="flex items-start gap-6" data-id="element-170">
                  <div className="w-2 h-2 rounded-full bg-[#6C2BD9] mt-3 shrink-0" data-id="element-171" />
                  <p className="text-2xl md:text-3xl text-slate-700 font-light leading-snug" data-id="element-172">
                    {bullet}
                  </p>
                </motion.div>)}
            </div>
            {slide.highlight && <motion.div variants={itemVariants} className="mt-12 p-6 bg-slate-50 border border-slate-100 rounded-xl inline-block self-start" data-id="element-173">
                <p className="text-xl md:text-2xl font-medium text-slate-800" data-id="element-174">
                  {slide.highlight}
                </p>
              </motion.div>}
            {slide.punchline && <motion.div variants={itemVariants} className="mt-16 pt-8 border-t border-slate-200" data-id="element-175">
                <p className="text-2xl md:text-3xl font-bold text-[#6C2BD9]" data-id="element-176">
                  <FormattedText text={slide.punchline} data-id="element-177" />
                </p>
              </motion.div>}
          </div>;
      case 'comparison':
        return <div className="flex flex-col justify-center h-full max-w-6xl mx-auto px-8 md:px-16 w-full" data-id="element-178">
            <motion.h2 variants={itemVariants} className="text-4xl md:text-5xl font-bold text-slate-900 tracking-tight mb-16 text-center" data-id="element-179">
              <FormattedText text={slide.headline || ''} data-id="element-180" />
            </motion.h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-16 md:gap-24 relative" data-id="element-181">
              {/* Divider for desktop */}
              <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-px bg-slate-200 -translate-x-1/2" data-id="element-182" />

              {/* Left Column */}
              <motion.div variants={itemVariants} className="flex flex-col" data-id="element-183">
                <h3 className="text-2xl font-semibold text-slate-400 mb-8 pb-4 border-b border-slate-100" data-id="element-184">
                  {slide.leftCol?.header}
                </h3>
                <div className="flex flex-col gap-6" data-id="element-185">
                  {slide.leftCol?.items.map((item, i) => <p key={i} className="text-xl md:text-2xl text-slate-500 font-light" data-id="element-186">
                      {item}
                    </p>)}
                </div>
              </motion.div>

              {/* Right Column */}
              <motion.div variants={itemVariants} className="flex flex-col" data-id="element-187">
                <h3 className="text-2xl font-semibold text-[#6C2BD9] mb-8 pb-4 border-b border-slate-100" data-id="element-188">
                  {slide.rightCol?.header}
                </h3>
                <div className="flex flex-col gap-6" data-id="element-189">
                  {slide.rightCol?.items.map((item, i) => <div key={i} className="flex items-center gap-4" data-id="element-190">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#6C2BD9]" data-id="element-191" />
                      <p className="text-xl md:text-2xl text-slate-800 font-medium" data-id="element-192">
                        {item}
                      </p>
                    </div>)}
                </div>
              </motion.div>
            </div>

            {slide.punchline && <motion.div variants={itemVariants} className="mt-20 text-center" data-id="element-193">
                <span className="inline-block px-8 py-4 bg-[#6C2BD9]/5 rounded-2xl" data-id="element-194">
                  <p className="text-2xl md:text-3xl font-bold text-[#6C2BD9]" data-id="element-195">
                    <FormattedText text={slide.punchline} data-id="element-196" />
                  </p>
                </span>
              </motion.div>}
          </div>;
      case 'closing':
        return <div className="flex flex-col items-center justify-center text-center h-full max-w-5xl mx-auto px-8 relative" data-id="element-197">
            <div className="flex flex-col gap-4 md:gap-6 mb-16" data-id="element-198">
              {slide.threeWords?.map((word, i) => <motion.h2 key={i} variants={itemVariants} className="text-5xl md:text-7xl font-bold text-slate-900 tracking-tight" data-id="element-199">
                  {word}
                </motion.h2>)}
            </div>

            {slide.finalLine && <motion.div variants={itemVariants} className="mt-8 mb-24" data-id="element-200">
                <p className="text-3xl md:text-4xl font-medium text-[#6C2BD9]" data-id="element-201">
                  <FormattedText text={slide.finalLine} data-id="element-202" />
                </p>
              </motion.div>}

            {slide.showLogo && <motion.div variants={itemVariants} className="absolute bottom-12" data-id="element-203">
                <SnakkerLogo className="!text-3xl" data-id="element-204" />
              </motion.div>}
          </div>;
      case 'calculator':
        return <CostCalculator data-id="element-205" />;
      default:
        return null;
    }
  };
  return <motion.div variants={containerVariants} initial="hidden" animate="show" className="w-full h-full" data-id="element-206">
      {renderContent()}
    </motion.div>;
};